/**
 * Turn a drop event's DataTransfer into a flat list of { file, relPath }
 * entries, descending into dropped folders. relPath is the path relative to
 * the drop target (e.g. "myFolder/sub/photo.jpg"), so uploads can preserve
 * folder structure.
 */
export const collectDroppedEntries = async dataTransfer => {
  const items = dataTransfer?.items
  // dataTransfer.items is only readable synchronously inside the drop event,
  // so grab every entry handle before the first await.
  const rootEntries = items
    ? Array.from(items)
        .filter(item => item.kind === 'file')
        .map(item => item.webkitGetAsEntry?.())
        .filter(Boolean)
    : []

  if (!rootEntries.length) {
    // Fallback for browsers without the entries API — flat files only.
    return Array.from(dataTransfer?.files || []).map(file => ({
      file,
      relPath: file.name,
    }))
  }

  const collected = []
  for (const entry of rootEntries) {
    await walkEntry(entry, '', collected)
  }
  return collected
}

const walkEntry = async (entry, parentPath, collected) => {
  const relPath = parentPath ? `${parentPath}/${entry.name}` : entry.name
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject))
    collected.push({ file, relPath })
  } else if (entry.isDirectory) {
    const reader = entry.createReader()
    // readEntries returns at most ~100 entries per call; loop until empty.
    for (;;) {
      const batch = await new Promise((resolve, reject) =>
        reader.readEntries(resolve, reject),
      )
      if (!batch.length) break
      for (const child of batch) {
        await walkEntry(child, relPath, collected)
      }
    }
  }
}
